import { List, ListItem, ListItemText, Menu, TextField } from '@mui/material';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (driver: PlanningsDriverEntity) => void;
  drivers: PlanningsDriverEntity[];
  /** 既に他 row で利用中の driver id 群 (= 選択肢から除外) */
  excludeDriverIds: ReadonlySet<number>;
  /** 現在この row が指している driver id (= 除外対象から外す) */
  currentDriverId: number | null;
};

const DriverSelectMenu: FC<Props> = memo(({ anchorEl, onClose, onSelect, drivers, excludeDriverIds, currentDriverId }) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    if (!anchorEl) {
      setInput('');
      setQuery('');
    }
  }, [anchorEl]);
  useEffect(() => { if (!composing) setQuery(input); }, [composing, input]);

  const handleSelect = useCallback((d: PlanningsDriverEntity) => {
    onSelect(d);
    onClose();
  }, [onSelect, onClose]);

  const filtered = drivers.filter((d) => {
    if (d.temporary) return false;
    if (d.id !== currentDriverId && excludeDriverIds.has(d.id)) return false;
    if (!query) return true;
    return (d.name ?? '').toLowerCase().includes(query.toLowerCase());
  });

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      sx={{ maxHeight: 500 }}
      slotProps={{ paper: { sx: { p: 0, m: 0 } } }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="検索"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onCompositionStart={() => setComposing(true)}
        onCompositionEnd={() => setComposing(false)}
      />
      <List sx={{ p: 0, m: 0, minWidth: 200 }}>
        {filtered.length === 0 && (
          <ListItem sx={{ fontSize: 12, color: 'text.secondary' }}>
            <ListItemText primary="選択可能なドライバーがありません" primaryTypographyProps={{ fontSize: 12 }} />
          </ListItem>
        )}
        {filtered.map((d) => (
          <ListItem
            key={`driver-${d.id}`}
            component="div"
            onClick={() => handleSelect(d)}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemText primary={d.name} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItem>
        ))}
      </List>
    </Menu>
  );
});

export default DriverSelectMenu;
